<template>
  <div class="companies-table">
    <div class="level mb-3">
      <div class="level-left">
        <button-group />
      </div>
      <div class="level-right">
        <button class="button is-primary" @click="openAddVideoOrderModal">
          + Create Video Order
        </button>
      </div>
    </div>
    <div class="table-container">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>ID</th>
            <th>User</th>
            <th>Camera</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Resolution</th>
            <th>Status</th>
            <th>Video Title</th>
            <th>Created At</th>
            <th></th>
          </tr>
        </thead>
        <!-- Skeleton Loading State -->
        <transition name="fade" mode="out-in">
          <tbody v-if="isLoadingSync" key="loading" class="skeleton-tbody">
            <tr
              v-for="n in skeletonRows"
              :key="`skeleton-${n}`"
              class="skeleton-row"
            >
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
            </tr>
          </tbody>

          <!-- Actual Data -->
          <tbody v-else-if="video_orders.length > 0" key="data" class="data-tbody">
            <tr v-for="(order, i) in video_orders" :key="order.id || i">
              <td>{{ order.id }}</td>
              <td :title="order.user ? order.user.email : ''">
                {{ order.user ? truncate(order.user.email, 25) : order.user_id }}
              </td>
              <td :title="order.camera ? order.camera.name : ''">
                {{ order.camera ? truncate(order.camera.name, 25) : order.camera_id }}
              </td>
              <td>{{ formatDate(order.start_date) }}</td>
              <td>{{ formatDate(order.end_date) }}</td>
              <td>
                <span class="tag is-light">{{ order.resolution || "—" }}</span>
              </td>
              <td>
                <span :class="statusClass(order)">{{ statusLabel(order) }}</span>
                <span
                  v-if="order.error"
                  class="icon has-text-danger"
                  :title="order.error"
                >
                  <i class="fas fa-exclamation-circle"></i>
                </span>
              </td>
              <td :title="order.video_title">
                {{ truncate(order.video_title || "", 30) }}
              </td>
              <td>{{ formatDate(order.created_at) }}</td>
              <td>
                <button
                  class="button is-small is-info"
                  :class="{ 'is-loading': queuingId === order.id }"
                  :disabled="order.status === 'queued' || order.status === 'completed' || queuingId === order.id"
                  @click="queue(order)"
                >
                  Queue
                </button>
              </td>
            </tr>
          </tbody>

          <!-- Empty State -->
          <tbody v-else key="empty">
            <tr>
              <td colspan="10" class="has-text-centered empty-state">
                <div class="empty-state-content">
                  <p class="has-text-grey">
                    <span class="icon is-large">
                      <i class="fas fa-film fa-2x"></i>
                    </span>
                  </p>
                  <p class="has-text-grey">No video orders found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </transition>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ButtonGroup from "@/components/ButtonGroup.vue";

export default {
  components: {
    "button-group": ButtonGroup,
  },
  data() {
    return {
      skeletonRows: 10,
      queuingId: null,
    };
  },
  computed: {
    ...mapState(["video_orders", "isLoadingSync"]),
  },
  methods: {
    ...mapActions(["fetchList", "queueVideoOrder", "openAddVideoOrderModal"]),
    async queue(order) {
      this.queuingId = order.id;
      await this.queueVideoOrder(order);
      this.queuingId = null;
    },
    statusClass(order) {
      if (order.deleted_at) return "tag is-danger";
      const map = {
        new: "tag is-light",
        queued: "tag is-info",
        completed: "tag is-success",
        failed: "tag is-danger",
        error: "tag is-danger",
        deleted: "tag is-danger",
      };
      return map[order.status] || "tag is-warning";
    },
    statusLabel(order) {
      if (order.deleted_at) return "deleted";
      return order.status || "—";
    },
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return isNaN(d.getTime()) ? "—" : d.toLocaleString();
    },
  },
  mounted() {
    this.fetchList();
    this.$root.$on("video-order-created", this.fetchList);
  },
  beforeDestroy() {
    this.$root.$off("video-order-created", this.fetchList);
  },
};
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 400px;
  transition: min-height 0.3s ease;
}

.skeleton-row {
  pointer-events: none;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  display: inline-block;
}

.skeleton-text {
  height: 20px;
  width: 80%;
  max-width: 120px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.empty-state {
  padding: 3rem !important;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.empty-state-content .icon {
  color: #dbdbdb;
}
</style>
